import { useEffect, useState, useRef, useCallback, useMemo } from "react"
import { useParams } from "react-router-dom"
import { fetchMenuByID } from "../../../utils/database"
import { getImage } from "../../../utils/filesystem"
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Styles from '../../../styles/Menu.module.css'
import Header from "./Components/Header"
import Product from "./Components/Product"
import Order from "./Components/Order";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import StoreNotFound from "./Components/StoreNotFound";
import EmptyStore from "./Components/EmptyStore";
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import useMediaQuery from '@mui/material/useMediaQuery';
import CategoryProducts from "./Components/CategoryProducts";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { DELIVERY_METHODS, STORE_STATES, USER_PLANS } from "../../../utils/structures";
import RoomServiceIcon from '@mui/icons-material/RoomService';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import FreeLimitExceeded from "./Components/FreeLimitExceeded";
import { useInView } from "react-intersection-observer";
import { useNavigate } from 'react-router-dom';

export default function Menu() {

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
    const bigScreen = useMediaQuery(theme.breakpoints.down('md'))

    const menuID = useParams().menuID
    const [menu, setMenu] = useState(undefined)
    const [order, setOrder] = useState({ total: 0, products: [] })
    const [showMessage, setShowMessage] = useState(false)

    const [storeState, setStoreState] = useState(true)
    const [storeIsOpen, setStoreIsOpen] = useState(true)

    const [deliveryMethod, setDeliveryMethod] = useState(null)
    const [showSelectDelivery, setShowSelectDelivery] = useState(true)

    const [categoriesProducts, setCategoriesProducts] = useState([])
    const [featuredProducts, setFeaturedProducts] = useState([])

    const categoriesRef = useRef([]);
    const [activeTab, setActiveTab] = useState(0);
    const [categoriesInView, setCategoriesInView] = useState([]);

    const slidesToShow = fullScreen ? 2 : bigScreen ? 3 : 4
    const sliderRef = useRef(null);
    const settings = {
        dots: false,
        arrows: true,
        infinite: true,
        slidesToShow: Math.min(slidesToShow, featuredProducts.length),
        slidesToScroll: 1,
    };

    const [ref, inView] = useInView({
        threshold: 0.2
    });

    const navigate = useNavigate()

    const isActiveBySchedule = useCallback(schedule => {
        const today = new Date()
        const todaySchedule = schedule[today.getDay()]

        if (todaySchedule.openAllDay) {
            return true

        } else {
            // Today
            const currentTime = today.getHours() * 100 + today.getMinutes()

            if (todaySchedule.time.length <= 1) {
                const openTime = parseInt(todaySchedule.time[0].start)
                const closeTime = parseInt(todaySchedule.time[0].end)

                if ((currentTime > openTime && currentTime < closeTime)
                    || (openTime > closeTime && currentTime <= 2400 && currentTime > openTime)) {
                    return todaySchedule.active ? true : false
                }

                // Yesterday
                const yesterday = new Date()
                yesterday.setDate(today.getDate() - 1)
                const yesterdaySchedule = schedule[yesterday.getDay()]

                const yesterdayCloseTime = parseInt(yesterdaySchedule.time[yesterdaySchedule.time.length - 1].end)
                const yesterdayOpenTime = parseInt(yesterdaySchedule.time[yesterdaySchedule.time.length - 1].start)

                if (yesterdayCloseTime < yesterdayOpenTime && currentTime < yesterdayCloseTime) {
                    return yesterdaySchedule.active ? true : false
                }

                return false

            } else {
                const openTime = [parseInt(todaySchedule.time[0].start), parseInt(todaySchedule.time[1].start)]
                const closeTime = [parseInt(todaySchedule.time[0].end), parseInt(todaySchedule.time[1].end)]

                if ((currentTime > openTime[0] && currentTime < closeTime[0])
                    || (currentTime > openTime[1] && currentTime < closeTime[1])
                    || (openTime[1] > closeTime[1] && currentTime <= 2400 && currentTime > openTime[1])) {
                    return todaySchedule.active ? true : false
                }

                // Yesterday
                const yesterday = new Date()
                yesterday.setDate(today.getDate() - 1)
                const yesterdaySchedule = schedule[yesterday.getDay()]

                const yesterdayCloseTime = parseInt(yesterdaySchedule.time[yesterdaySchedule.time.length - 1].end)
                const yesterdayOpenTime = parseInt(yesterdaySchedule.time[yesterdaySchedule.time.length - 1].start)

                if (yesterdayCloseTime < yesterdayOpenTime && currentTime < yesterdayCloseTime) {
                    return yesterdaySchedule.active ? true : false
                }

                return false
            }
        }
    }, [])

    const menuFetch = useCallback(async () => {
        const data = await fetchMenuByID(menuID)
        setMenu(data)

        if (data.message.msg !== '' && data.plan.type !== USER_PLANS.free && isActiveBySchedule(data.message.schedule)) {
            setShowMessage(true)
        }
    }, [menuID, isActiveBySchedule])

    const getCategoryProducts = useCallback(category => {
        return Object.entries(menu.products).filter(product => product[1].category === category)
    }, [menu])

    useEffect(() => {
        if (menu && menu.title !== '') {
            document.title = menu.title + ' | Tookeat'
        }
    }, [menu])

    useEffect(() => {
        setStoreIsOpen(
            storeState.delivery === STORE_STATES.open
            || storeState.delivery === STORE_STATES.openSinceYesterday
            || storeState.delivery === STORE_STATES.openAllDay
            || storeState.takeaway === STORE_STATES.openAllDay
            || storeState.takeaway === STORE_STATES.open
            || storeState.takeaway === STORE_STATES.openSinceYesterday
        )
    }, [storeState])

    useEffect(() => {

        if (menu && menu.products && categoriesProducts.length === 0 && menu.categories) {

            let aux = []
            for (let i = 0; i < menu.categories.length; i++) {

                const products = getCategoryProducts(menu.categories[i])
                if (products.length > 0) {
                    aux = aux.concat({ category: menu.categories[i], products: products })
                }
            }
            categoriesRef.current = categoriesRef.current.slice(0, aux.length);

            const products = Object.entries(menu.products).filter(product => product[1].featured)

            setFeaturedProducts(products)
            setCategoriesProducts(aux)
        }

    }, [menu, categoriesProducts, getCategoryProducts])

    useEffect(() => {
        menuFetch()
    }, [menuFetch])

    const getSubOptionsNames = (options) => {
        let subOptions = []
        for (let i = 0; i < options.length; i++) {
            for (let j = 0; j < options[i].subOptions.length; j++) {
                subOptions.push(options[i].subOptions[j].name)
            }
        }
        return subOptions
    }

    const addProductToOrder = useCallback(product => {
        const productIndex = order.products.findIndex(prod => prod.id === product.id);
        let newOrder = { products: [], total: order.total };

        if (productIndex !== -1) {
            const subOptions1 = getSubOptionsNames(product.options);
            const subOptions2 = getSubOptionsNames(order.products[productIndex].options);

            const equals = subOptions1.length === subOptions2.length
                && subOptions1.every(option => subOptions2.includes(option))
                && product.note === ''
                && order.products[productIndex].note === ''

            if (equals) {
                newOrder.products = order.products.slice();
                newOrder.products[productIndex].count += 1;
            } else {
                newOrder.products = [...order.products, product];
            }
        } else {
            newOrder.products = [...order.products, product];
        }

        newOrder.total = order.total + (product.price + product.extraPrice) * product.count;
        setOrder(newOrder);
    }, [order])

    const removeProductFromOrder = useCallback(productIndex => {
        let newOrder = { products: [], total: order.total - order.products[productIndex].price - order.products[productIndex].extraPrice };
        newOrder.products = [...order.products]
        newOrder.products.splice(productIndex, 1)
        setOrder(newOrder)
    }, [order])

    const changeProductAmount = useCallback((productIndex, amount) => {
        let newOrder = { products: [], total: order.total + amount * (order.products[productIndex].price + order.products[productIndex].extraPrice) }
        newOrder.products = [...order.products]
        newOrder.products[productIndex].count += amount
        setOrder(newOrder)
    }, [order])


    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        scrollToContent(newValue)
    };

    const scrollToContent = (index) => {
        const contentRef = categoriesRef.current[index];

        if (contentRef) {
            window.scrollTo({
                top: contentRef.offsetTop,
                behavior: 'smooth',
            });
        }
    };

    const getProductImg = useCallback(async product => {
        return await getImage(menu.userid + '/' + product[0] + '/01.jpg')
    }, [menu])

    const availableDeliveryOptions = useMemo(() => {
        return !menu ? null : !menu.deliveryOptions ? null : menu.deliveryOptions.map((option, i) => {
            switch (option) {
                case DELIVERY_METHODS.delivery:
                    if (storeState.delivery === STORE_STATES.open
                        || storeState.delivery === STORE_STATES.openSinceYesterday
                        || storeState.delivery === STORE_STATES.openAllDay)
                        return <ToggleButton key={i} value={option}>Delivery <DeliveryDiningIcon /></ToggleButton>

                    else {
                        return <ToggleButton key={i} disabled value={option}>Delivery (Cerrado)<DeliveryDiningIcon /></ToggleButton>
                    }

                case DELIVERY_METHODS.inPlace:
                    if (storeState.takeaway === STORE_STATES.open
                        || storeState.takeaway === STORE_STATES.openSinceYesterday
                        || storeState.takeaway === STORE_STATES.openAllDay)
                        return <ToggleButton key={i} value={option}>Comer en el lugar <RoomServiceIcon /></ToggleButton>

                    else {
                        return <ToggleButton key={i} disabled value={option}>Comer en el lugar (Cerrado)<RoomServiceIcon /></ToggleButton>
                    }

                case DELIVERY_METHODS.takeaway:
                    if (storeState.takeaway === STORE_STATES.open
                        || storeState.takeaway === STORE_STATES.openSinceYesterday
                        || storeState.takeaway === STORE_STATES.openAllDay)
                        return <ToggleButton key={i} value={option}>Para retirar <ShoppingBagIcon /></ToggleButton>

                    else {
                        return <ToggleButton key={i} disabled value={option}>Takeaway (Cerrado)<ShoppingBagIcon /></ToggleButton>
                    }
                default:
                    return null

            }
        })
    }, [menu, storeState])


    return (
        <>
            {menu === undefined ? null :
                menu === null ? <StoreNotFound /> :
                    menu.products && menu.plan.type === USER_PLANS.free && Object.values(menu.products).length > 10 ? <FreeLimitExceeded /> :

                        <main className={Styles.container}
                            style={{ backgroundColor: menu.color >= 0 ? 'hsl(' + menu.color + ', 100%, 99%)' : 'hsl(0, 0%, 99%)' }}>
                            <div className={Styles.menuContainer}>
                                <Header menu={menu} setStoreState={setStoreState} />

                                {!menu.products ? <EmptyStore /> : menu.products.length <= 0 ? <EmptyStore /> :
                                    <>
                                        <Tabs
                                            variant="scrollable"
                                            scrollButtons="auto"
                                            value={activeTab}
                                            onChange={handleTabChange}
                                            className={Styles.categoriesTabs}
                                            sx={{ backgroundColor: menu.color >= 0 ? 'hsl(' + menu.color + ', 100%, 99%)' : 'hsl(0, 0%, 99%)' }}
                                        >
                                            {categoriesProducts.map((category, index) => {
                                                return (
                                                    <Tab key={index} label={category.category} />
                                                )
                                            })}
                                        </Tabs>

                                        <Stack className={Styles.categoriesContainer}>

                                            {featuredProducts.length === 0 || menu.plan.type === USER_PLANS.free ? null :
                                                <>
                                                    <Stack direction='row' justifyContent='space-between' alignItems='center'>
                                                        <Typography variant="h4">
                                                            Destacados
                                                        </Typography>

                                                        <div>
                                                            <IconButton
                                                                disabled={slidesToShow >= featuredProducts.length}
                                                                onClick={() => sliderRef?.current?.slickPrev()}>
                                                                <ArrowBackIosOutlinedIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                disabled={slidesToShow >= featuredProducts.length}
                                                                onClick={() => sliderRef?.current?.slickNext()}>
                                                                <ArrowForwardIosOutlinedIcon />
                                                            </IconButton>
                                                        </div>
                                                    </Stack>

                                                    <Slider {...settings} ref={sliderRef} className={Styles.slider}>
                                                        {!featuredProducts ? null :
                                                            featuredProducts.map((product, i) => {
                                                                const offer = product[1].offer ? menu.offers[menu.offers.findIndex(offer => offer.id === product[1].offer)] : null

                                                                return (
                                                                    <div key={i}>
                                                                        <Product
                                                                            offer={offer && isActiveBySchedule(offer.schedule) ? offer : null}
                                                                            featuredVersion={true}
                                                                            product={product}
                                                                            getProductImg={getProductImg}
                                                                            addProductToOrder={addProductToOrder}
                                                                            color={menu.color}
                                                                            storeIsOpen={storeIsOpen}
                                                                        />
                                                                    </div>
                                                                )
                                                            })}
                                                    </Slider>
                                                </>}

                                            {!categoriesProducts ? null :
                                                categoriesProducts.map((categoryProducts, index) => {
                                                    return (
                                                        <div key={index} ref={el => categoriesRef.current[index] = el} >
                                                            <CategoryProducts
                                                                isActiveBySchedule={isActiveBySchedule}
                                                                products={categoryProducts.products}
                                                                category={categoryProducts.category}
                                                                menu={menu}
                                                                addProductToOrder={addProductToOrder}
                                                                storeIsOpen={storeIsOpen}
                                                                toggleActiveTab={state => {
                                                                    let currentState = categoriesInView
                                                                    currentState[index] = state

                                                                    const stateIndex = currentState.findIndex(state => state)
                                                                    setActiveTab(stateIndex >= 0 ? stateIndex : 0)
                                                                    setCategoriesInView(currentState)
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                })}
                                        </Stack>

                                        {!inView ? <div style={{ height: '50px' }}></div> : null}
                                        <span ref={ref}> </span>

                                        {order.products.length <= 0 ? null :
                                            <Order
                                                order={order}
                                                menu={menu}
                                                removeProductFromOrder={removeProductFromOrder}
                                                changeProductAmount={changeProductAmount}
                                                displayRelative={inView}
                                            />}


                                        <Dialog
                                            fullScreen={false}
                                            open={showMessage}
                                            onClose={() => setShowMessage(false)}
                                            aria-labelledby="responsive-dialog-title"
                                        >
                                            <DialogTitle id="responsive-dialog-title">
                                                {"Mensaje de " + menu.title}
                                            </DialogTitle>
                                            <DialogContent>
                                                <DialogContentText>
                                                    {menu.message.msg}
                                                </DialogContentText>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button autoFocus onClick={() => setShowMessage(false)}>
                                                    Entendido!
                                                </Button>
                                            </DialogActions>
                                        </Dialog>

                                        <Dialog
                                            fullScreen={false}
                                            open={showSelectDelivery}
                                            onClose={() => setShowSelectDelivery(false)}
                                        >
                                            <DialogTitle fontWeight={500} textAlign='center'>
                                                Elegir metodo de entrega
                                            </DialogTitle>
                                            <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>

                                                {!menu.deliveryOptions ? <Typography variant="h6">Sin metodos de entrega disponibles</Typography> :
                                                    <ToggleButtonGroup
                                                        fullWidth
                                                        orientation="vertical"
                                                        color="primary"
                                                        value={deliveryMethod}
                                                        exclusive
                                                        onChange={(event, newAlignment) => {
                                                            if (newAlignment !== null) {
                                                                setDeliveryMethod(newAlignment)
                                                            }
                                                        }}
                                                        sx={{ marginBottom: '15px' }}
                                                    >
                                                        {availableDeliveryOptions}
                                                    </ToggleButtonGroup>
                                                }

                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={() => setShowSelectDelivery(false)} variant="outlined">
                                                    Solo ver el menu
                                                </Button>
                                                <Button disabled={!deliveryMethod} onClick={() => setShowSelectDelivery(false)} variant="contained">
                                                    Confirmar
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                    </>}
                                <div className={Styles.tookeatAd}>
                                    <Typography onClick={() => navigate('/')}>Menu creado con <b>Tookeat</b></Typography>
                                </div>
                            </div>
                        </main>
            }
        </>
    )
}